import { Questionnaire, Question } from "models/Questionnaire";
import React, { useContext } from "react";
import { Row, Button, Col, Collapse } from "react-bootstrap";
import { AnswerData, ResultPacket, PreliminaryData } from "models/ResultPacket/ResultPacket";
import l10n, { LanguageCode } from "localization/LocalizationData";
import { PreliminaryField } from "components/common/PreliminaryField";
import { RouteComponentProps, Redirect } from "react-router";
import QuestionnaireContext from "contexts/QuestionnaireContext";
import ResultService from "services/ResultService";
import { Result } from "models/Result";
import { ValueResource } from "models/QuestionnaireResource";
import { NoAnswer } from "models/ResultPacket/Preliminary";
import { Container } from "react-bootstrap";
import { QuestionField } from "components/common/QuestionField";
import LoadingView from "./LoadingView";
import starred from 'images/starred.png';
import unstarred from 'images/unstarred.png';
import { onKeyPress } from "Utilities/AriaKeyPress";

export const FinishView = (props: RouteComponentProps<{}>) => {
	const context = useContext(QuestionnaireContext);
	const questionnaire = context.questionnaire;
	const [sending, setSending] = React.useState(false);
	const [highlightError, setHighlightError] = React.useState(false);

	React.useEffect(() => {
		if(context.lastState !== props.location.pathname)
			context.setLastState(props.location.pathname);
	})

	if (questionnaire === null) {
		// Virhe, josta ei voi selviytyä
		return <p>Tästä virheestä ei voi selviytyä</p>
	}
	const answers = context.answers;
	const preliminary = context.preliminary;

	for (let i = 0; i < questionnaire.questions.length; i++) {
		if (answers[questionnaire.questions[i].id] === undefined) {
			return <Redirect to={`/questionnaire/${questionnaire.id}/question/${i}`} />
		}
	}

	if (context.preliminaryFilled === false) {
		return <Redirect to={`/questionnaire/${context.questionnaire?.id}/preliminary`} />
	}

	if (context.canJumpToSummary === false) {
		context.setCanJumpToSummary(true);
	}

	function navigateTo(index: number) {
		props.history.push(`/questionnaire/${questionnaire?.id}/question/${index}`);
	}

	function navigateBack() {
		props.history.push(`/questionnaire/${questionnaire?.id}/preliminary`);
	}

	function submit() {
		if (context.getHighlightCount() < 1 || context.getHighlightCount() > 3) {
			setHighlightError(true);
			return;
		}
		const answers = Object.entries(context.answers).filter(x => x[1] !== undefined)
			.flatMap(x => x[1]) as AnswerData[];

		const prems: PreliminaryData = {};
		Object.entries(context.preliminary).forEach((x: [string, any]) => {
			if (x[1].value !== NoAnswer) {
				prems[x[0] as keyof PreliminaryData] = x[1];
			}
		})

		const result: ResultPacket = {
			language: questionnaire?.language as LanguageCode,
			id: (questionnaire as Questionnaire).id,
			answers: answers,
			preliminary: prems
		}
		if (context.token !== null) {
			setSending(true);
			ResultService.post(result, context.token)
				.then((result: Result) => {
					props.history.push(`/questionnaire/${questionnaire?.id}/result`,
						{
							result: result,
							code: context.token
						});
				}).catch((reason: any) => {
					setSending(false);
					console.log("Lähetys epäonnistui");
					//TODO hallitse vikatilat
				});
		}
	}

	function answerOnKeyPressed(evt: React.KeyboardEvent<HTMLDivElement>, index: number) {
		evt.preventDefault();
		if (onKeyPress(evt)) {
			navigateTo(index);
		}
	}

	function onHighlightToggled(question: Question, value: boolean) {
		setHighlightError(false);
		context.setHighlight(question, value);
	}

	if (sending) {
		return (
			<Container>
				<LoadingView title={l10n.loading} />
			</Container>
		)
	}

	return (
		<Container>
			<h1 className='mt-4'>{l10n.finish_title}</h1>
			<Row>
				<Col className='finish-description'>
					<p><b>{l10n.finish_description_start}</b> {l10n.finish_description}</p>
				</Col>
			</Row>
			<PreliminaryField preliminaries={preliminary} />
			<Row>
				<Col>
					<h2>{l10n.finish_answers_title}</h2>
				</Col>
			</Row>
			<div className='mb-4 finish-answer-list'>
				{
					questionnaire.questions.map((question, index) => {
						const highlighted = context.answers[question.id]?.highlighted === true;
						return (
							<Col key={`answer${question.id}`}>
								<Row className='py-3 finish-answer-row mx-0' tabIndex={0}
									onKeyPress={(evt: React.KeyboardEvent<HTMLDivElement>) => answerOnKeyPressed(evt, question.index)}
									onClick={() => navigateTo(question.index)} >

									<Col md='3' xs='12'><span className='mb-md-0 mb-3'>{question.title}</span></Col>
									{
										questionnaire.type === 'Spiral' &&
										<React.Fragment>
											<Col className='d-flex justify-content-center px-md-0' xs='12' md='6'><QuestionField idPrefix={question.id} currentValue={answers[question.id].primary}
												answerValue={question.category.values?.primary as ValueResource}
												disabled={true}
												title={question.category.questiontitle} />
											</Col>
											<Col xs='12' md='3' className='finish-secondary-answer mt-2'>
												<Col xs='12'><p className='mb-1'>{question.category.values?.secondary?.title} </p></Col>
												<Col xs='12'><p className='mb-0'><b>{question.category.values?.secondary?.values.find(x => x.value === answers[question.id].secondary)?.text}</b></p></Col>
											</Col>
										</React.Fragment>
									}
								</Row>
								<Row className='highlight-container'>
									<div className='question-separator' />
									<button className='question-highlight'
										onClick={() => onHighlightToggled(question, !highlighted)}>
										<img className='highlighted-img mr-4' alt={`${highlighted ? 'merkitty muutoskohteeksi' : 'ei merkitty muutoskohteeksi'}`} src={highlighted ? starred : unstarred} />
										<p>{highlighted ? l10n.finish_unhighlight : l10n.finish_highlight}</p>
									</button>
								</Row>
							</Col>
						)
					})
				}
			</div>
			<Row className='invalid-answer-alert-container'>
				<Collapse in={highlightError}>
					<div>
						<p role='alert' className='question-invalid-answer-alert'><b>{l10n.finish_highlightError}</b></p>
					</div>
				</Collapse>
			</Row>
			<Row className='justify-content-center mb-4'>
				<Col xs='auto'>
					<Button onClick={() => navigateBack()}>{l10n.button_previous}</Button>
				</Col>
				<Col xs='auto'>
					<Button disabled={highlightError} onClick={() => submit()}>{l10n.button_send}</Button>
				</Col>
			</Row>

		</Container >
	);
}